import type { Money as MoneyValue } from '@/lib/money';
import { Money } from './Money';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';

export interface BillRow {
  label: string;
  amount: MoneyValue;
}

/**
 * Shows server-provided bill figures only. The frontend never adds, subtracts or rounds.
 * A discount row appears only when the server sent one and it is not zero.
 */
export function BillSummary({
  subtotal,
  taxes = [],
  discount,
  total,
}: {
  subtotal: MoneyValue;
  taxes?: BillRow[];
  discount?: MoneyValue | null;
  total: MoneyValue;
}) {
  const showDiscount = discount != null && discount.amount !== 0;
  return (
    <dl className={cn(cardClasses(), 'text-sm')}>
      <Row label="Subtotal" amount={subtotal} />
      {taxes.map((tax) => (
        <Row key={tax.label} label={tax.label} amount={tax.amount} />
      ))}
      {showDiscount && (
        <Row label="Discount" amount={discount} prefix={discount.amount > 0 ? '−' : undefined} />
      )}
      <div className="mt-2 flex justify-between border-t border-line pt-3 text-base font-semibold text-ink">
        <dt>Total</dt>
        <dd>
          <Money value={total} />
        </dd>
      </div>
    </dl>
  );
}

/** `prefix` is a sign shown before the server's amount (a discount reads "−₹50.00"); the amount is unchanged. */
function Row({ label, amount, prefix }: { label: string; amount: MoneyValue; prefix?: string }) {
  return (
    <div className="flex justify-between py-1 text-muted">
      <dt>{label}</dt>
      <dd>
        {prefix}
        <Money value={amount} />
      </dd>
    </div>
  );
}
